// Single source of truth for the "Create Draft" workflow picker: which pdfType each
// workflow applies to, what it's labelled in the dropdown, and which subject prefix /
// message template it resolves to. messageType values reuse the existing invoice
// messagePrefixes strings where a category already had one, so getPdfEmailText's
// existing branches keep working unchanged for those.

export const WORKFLOWS = [
  {
    key: "quote_general",
    pdfType: "Quote",
    label: "General",
    subjectPrefix: "",
    messageType: "General (Quote)",
  },
  {
    key: "quote_eicr",
    pdfType: "Quote",
    label: "EICR",
    subjectPrefix: " EICR",
    messageType: "EICR (Quote)",
  },
  {
    key: "quote_pat",
    pdfType: "Quote",
    label: "PAT",
    subjectPrefix: " PAT",
    messageType: "PAT (Quote)",
  },
  {
    key: "quote_eicr_pat",
    pdfType: "Quote",
    label: "EICR & PAT",
    subjectPrefix: " EICR & PAT",
    messageType: "EICR & PAT (Quote)",
  },
  {
    key: "quote_eicr_remedials",
    pdfType: "Quote",
    label: "EICR Remedials",
    subjectPrefix: " EICR Remedials",
    messageType: "EICR Remedials (Quote)",
  },
  {
    key: "quote_smoke_alarms",
    pdfType: "Quote",
    label: "Smoke Alarms",
    subjectPrefix: " Fire Alarm",
    messageType: "S/A (Quote)",
  },
  {
    key: "invoice_general",
    pdfType: "Invoice",
    label: "General",
    subjectPrefix: "",
    messageType: "General (Invoice)",
  },
  {
    key: "invoice_eicr",
    pdfType: "Invoice",
    label: "EICR",
    subjectPrefix: " EICR",
    messageType: "EICR (Invoice)",
  },
  {
    key: "invoice_smoke_alarms",
    pdfType: "Invoice",
    label: "Smoke Alarms",
    subjectPrefix: " Fire Alarm",
    messageType: "S/A (Invoice)",
  },
  {
    key: "invoice_eicr_pat",
    pdfType: "Invoice",
    label: "EICR & PAT",
    subjectPrefix: " EICR & PAT",
    messageType: "EICR & PAT (Invoice)",
  },
  {
    key: "invoice_pat",
    pdfType: "Invoice",
    label: "PAT",
    subjectPrefix: " PAT",
    messageType: "PAT (Invoice)",
  },
  {
    key: "invoice_eicr_remedials",
    pdfType: "Invoice",
    label: "EICR Remedials",
    subjectPrefix: " EICR Remedials",
    messageType: "EICR Remedials (Invoice)",
  },
];

// Which paid-invoice categories should also auto-tick "Cert" when their draft is confirmed
// sent - because a certificate genuinely gets sent along with those specific paid invoices.
// General doesn't produce a certificate, so it's deliberately excluded - an allowlist rather
// than "everything except General" so a future category defaults to NOT ticking Cert unless
// explicitly added here.
export const CERT_ELIGIBLE_WORKFLOW_KEYS = new Set([
  "invoice_eicr",
  "invoice_eicr_remedials",
  "invoice_pat",
  "invoice_eicr_pat",
  "invoice_smoke_alarms",
]);

export function getWorkflow(workflowKey) {
  return WORKFLOWS.find((w) => w.key === workflowKey);
}

export function getWorkflowsForType(pdfType) {
  return WORKFLOWS.filter((w) => w.pdfType === pdfType);
}

export function defaultWorkflowKeyForType(pdfType) {
  return pdfType === "Quote" ? "quote_general" : "invoice_general";
}

// Best-effort keyword match from a timesheet's free-text jobDetails to the closest workflow
// of the given pdfType, used only to pick a sensible *default* workflow when the email box
// first opens - never overrides a workflow someone's already picked from the dropdown, and
// staff can always change it manually if this guesses wrong. Deliberately simple regex
// matching rather than an AI call: the vocabulary here (EICR/PAT/Smoke Alarms) is small and
// fixed, unlike thread-matching where every email's content is different free text. Checked
// most-specific first so e.g. "EICR & PAT" doesn't fall through to matching plain "EICR".
const JOB_DETAILS_WORKFLOW_MATCHERS = [
  { suffix: "eicr_pat", regex: /\beicr\b[\s\S]*\bpat\b|\bpat\b[\s\S]*\beicr\b|eicr\s*(&|and|\+)\s*pat/i },
  { suffix: "eicr_remedials", regex: /\beicr\b[\s\S]*remedial|remedial[\s\S]*\beicr\b/i },
  { suffix: "eicr", regex: /\beicr\b/i },
  { suffix: "pat", regex: /\bpat\b/i },
  { suffix: "smoke_alarms", regex: /smoke\s*alarms?|smoke\s*det|fire\s*alarm|\bfire\b|\bf\/?a\b|\bs\/?alarms?\b/i },
];

// Builds a workflow key from an already-known job-type category (e.g. "eicr") and a pdfType,
// the same ${prefix}_${suffix} pattern inferWorkflowKeyFromJobDetails uses below - returns
// null if that combination doesn't correspond to a real workflow. Used to prefer the Gmail
// Add-on's AI-classified jobTypeCategory (see emailFieldExtractor.js) over the keyword
// matcher below when a primary has one - see PdfTimesheet.jsx's initialWorkflowKey.
export function workflowKeyForCategory(category, pdfType) {
  if (!category) return null;
  let prefix = pdfType === "Quote" ? "quote" : "invoice";
  let candidateKey = `${prefix}_${category}`;
  return getWorkflow(candidateKey) ? candidateKey : null;
}

// Inverse of workflowKeyForCategory - strips the quote_/invoice_ prefix off a workflow key
// to get back the job-type category (e.g. "eicr") stored on the primary as jobTypeCategory,
// mirroring emailFieldExtractor.js's JOB_TYPE_CATEGORIES on the server. Used to write the
// workflow the user picked in the "Create Draft" dropdown back onto the primary.
export function categoryForWorkflowKey(workflowKey) {
  let workflow = getWorkflow(workflowKey);
  if (!workflow) return null;
  let prefix = workflow.pdfType === "Quote" ? "quote_" : "invoice_";
  return workflow.key.slice(prefix.length);
}

export function inferWorkflowKeyFromJobDetails(jobDetails, pdfType) {
  let fallback = defaultWorkflowKeyForType(pdfType);
  if (!jobDetails) return fallback;

  let prefix = pdfType === "Quote" ? "quote" : "invoice";

  for (let { suffix, regex } of JOB_DETAILS_WORKFLOW_MATCHERS) {
    let candidateKey = `${prefix}_${suffix}`;
    if (regex.test(jobDetails) && getWorkflow(candidateKey)) {
      return candidateKey;
    }
  }

  return fallback;
}

// Identifies which stored/editable template a given (workflow, emailType, vatMode)
// combination should use - see src/helperFunctions/defaultTemplates.js for the full set.
// Every emailType now has its own vat_on/vat_off pair, not just Quote.
export function getTemplateKey(workflowKey, emailType, vatMode) {
  let base = emailType === "Paid" ? `${workflowKey}__paid` : workflowKey;
  return `${base}__${vatMode ? "vat_on" : "vat_off"}`;
}
